/**
 * The frame every page sits in: the video background, the header, the routed page, the footer.
 *
 * Ports `app/layout.tsx`, `components/layout/Header.tsx` and `components/layout/Footer.tsx`.
 * The header and footer live here together rather than in three files because between them
 * they are one bar of navigation and one bar of credits — splitting them made sense when
 * Next.js wanted a `layout.tsx` per segment, and does not now.
 *
 * Two behaviours are carried over deliberately:
 *
 * - **A failed logo degrades to a wordmark.** A missing asset must not leave a broken-image
 *   box in the chrome of every page.
 * - **The admin links are cosmetic.** They appear for a caller `GET /access/me` calls an
 *   admin, but `require_admin` on every admin route is the actual enforcement —
 *   revealing the links to somebody else would grant nothing. Deciding otherwise would put an
 *   access-control decision in a navigation component.
 */

import { AppBar, Box, Button, Container, Stack, Toolbar, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { useMyAccess } from "@/api/queries";
import { useAuth } from "@/auth/AuthProvider";
import { BRAND_LOGO_SRC, BRAND_NAME, BRAND_TAGLINE } from "@/constants/brand";
import { jp } from "@/theme/tokens";

import { VideoBackground } from "./VideoBackground";

const NAV_ITEMS = [
  { to: "/generate", label: "Generate" },
  { to: "/history", label: "History" },
  { to: "/about", label: "About" },
] as const;

const ADMIN_NAV_ITEMS = [
  { to: "/admin/clients", label: "Clients" },
  { to: "/admin/history", label: "All history" },
  { to: "/admin/usage", label: "Usage" },
  { to: "/admin/governance", label: "Governance" },
  { to: "/admin/register", label: "Register" },
] as const;

/**
 * Whether to show the admin links — asked of the backend (`GET /access/me`).
 *
 * It used to read the `role: "admin"` claim off the token. Under Nexus (W1) nobody carries that
 * claim, and admin is a permission only the backend can ask Nexus about, so the header asks the
 * same function `require_admin` uses. A failure hides the links, which is the safe direction:
 * every admin route enforces for itself.
 */
function useIsAdmin(): boolean {
  const { user } = useAuth();
  const { data } = useMyAccess();
  // The signed-out case is derived rather than trusted from a cached answer: a query for the
  // previous user must not show admin links between signing out and the cache catching up.
  return Boolean(user) && data?.isAdmin === true;
}

function BrandMark({ className }: { className?: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <Typography component="span" className="font-semibold text-white">
        {BRAND_NAME}
      </Typography>
    );
  }
  return (
    <img
      src={BRAND_LOGO_SRC}
      alt={`${BRAND_NAME} logo`}
      className={className}
      onError={() => setFailed(true)}
    />
  );
}

/**
 * Whether an element's content is wider than the element — i.e. whether it is scrolling.
 *
 * A `ResizeObserver` rather than a mount-time measurement plus a resize listener. The first
 * version of this did the latter and reported `false` on a viewport where the nav was plainly
 * overflowing: the measurement ran before the webfont had swapped and the buttons settled at
 * their real width, and nothing afterwards asked again. The observer catches that, the admin
 * links arriving once `GET /access/me` answers, and an ordinary viewport resize, without
 * anyone having to enumerate the triggers correctly.
 *
 * It watches the element's own box, so a scroll does not re-fire it — only an actual change of
 * size does.
 */
function useOverflowing<T extends HTMLElement>(): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    // The children carry the width that overflows, so a change in *their* size (a font swap,
    // a label change) matters as much as a change in the container's.
    for (const child of el.children) observer.observe(child);
    return () => observer.disconnect();
  });

  return [ref, overflowing];
}

function Header() {
  const { user, loading, signOut } = useAuth();
  const isAdmin = useIsAdmin();
  const location = useLocation();
  const navItems = isAdmin ? [...NAV_ITEMS, ...ADMIN_NAV_ITEMS] : NAV_ITEMS;
  const [navRef, navOverflowing] = useOverflowing<HTMLDivElement>();

  return (
    <AppBar
      position="static"
      elevation={0}
      // `AppBar` is a `Paper`, so without this it inherits the card treatment from the theme's
      // `MuiPaper` override — the radius, the hairline border all round and the card shadow —
      // and the header read as one more rounded card floating on the page. It is chrome, not
      // content: square, edge to edge, with a single hairline underneath.
      sx={{
        backgroundColor: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(12px)",
        borderRadius: 0,
        border: 0,
        borderBottom: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "none",
      }}
    >
      {/*
        `xl`, not the page's `lg`. An admin sees eight links plus the account controls, which is
        wider than 1200px less the logo — at `lg` the nav scrolled even on a full-width desktop
        monitor. The bar is chrome, so it can be wider than the content column beneath it.
      */}
      <Container maxWidth="xl" disableGutters>
        {/*
          One line, at every width.

          This used to wrap: below `sm` the nav dropped to its own full-width row under the
          logo, which cost a second bar of vertical space on exactly the screens with least of
          it, and made the header's height change as you resized. The constraint behind that is
          real — the eight pills an admin sees are wider than a 375px viewport — but wrapping is
          not the only answer to it. The nav scrolls horizontally instead (below), so the bar
          stays one line and an admin on a phone swipes the nav rather than losing a row.
        */}
        <Toolbar
          disableGutters
          className="flex flex-nowrap items-center justify-between gap-x-2 px-4 py-3 sm:gap-x-4 sm:px-6"
        >
          <NavLink to="/" className="flex shrink-0 items-center gap-2">
            {/* h-5 below `sm`: this is a wordmark, not a monogram, so at h-6 it takes 125px —
                a third of a 390px bar — and that width comes straight out of the nav's. */}
            <BrandMark className="h-5 w-auto brightness-0 invert sm:h-6" />
          </NavLink>

          {/*
            `overflow-x-auto` with `shrink-0` children is the backstop for the narrowest phones:
            the links keep their real width and the strip scrolls, rather than being squeezed
            into unreadable slivers. `min-w-0` lets this flex child actually shrink inside the
            toolbar — without it the row would simply overflow the bar. The scrollbar itself is
            hidden; the row is short and an always-visible bar under three links is noise.
          */}
          <Box
            className="relative min-w-0"
            // A fade on the trailing edge, drawn only while the strip actually overflows.
            // Without it a scrolled-away "About" is simply invisible — there is no scrollbar
            // (hidden below) and no other cue, which is a worse failure than the wrapped row
            // this replaced. `mask-image` fades the content itself, so it works over the
            // translucent bar without painting a gradient swatch on top of it.
            sx={{
              "&:has(nav[data-overflowing='true'])": {
                maskImage: "linear-gradient(to right, #000 calc(100% - 24px), transparent)",
              },
            }}
          >
          <Stack
            component="nav"
            direction="row"
            spacing={0.5}
            className="min-w-0 flex-nowrap overflow-x-auto [&>*]:shrink-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            aria-label="Main"
            ref={navRef}
            data-overflowing={navOverflowing ? "true" : "false"}
          >
            {navItems.map((item) => {
              const active = location.pathname.startsWith(item.to);
              return (
                <Button
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  size="small"
                  // `inherit`, not the default `primary`: the theme darkens the label of every
                  // text/outlined primary button for contrast on a *light* card, and that rule
                  // outranks the `sx` colour below — so on this black bar the links rendered
                  // navy-on-black. These links set their own colour; they want no part of it.
                  color="inherit"
                  sx={{
                    color: active ? jp.ink : "rgba(255,255,255,0.85)",
                    backgroundColor: active ? "#FFFFFF" : "transparent",
                    // The theme's 20px inline padding suits a page button; on a nav pill it is
                    // 40px of the ~80px each link occupies, which is what pushed the strip past
                    // the bar on a phone in the first place.
                    paddingInline: { xs: 1.25, sm: 2 },
                    "&:hover": {
                      backgroundColor: active ? "#FFFFFF" : "rgba(255,255,255,0.12)",
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>
          </Box>

          <Box className="flex shrink-0 items-center gap-3">
            {loading ? null : user ? (
              <>
                {/* From 1440px only. A full address is ~300px, and below that it is exactly what
                    pushes an admin's eight links into scrolling — at 1280px, by 15px. Without it
                    they fit from 1024px up. Being signed in is already evident from the Sign out
                    button beside it. */}
                <Typography variant="body2" className="hidden text-white/70 min-[1440px]:block">
                  {user.email}
                </Typography>
                <Button size="small" variant="outlined" color="inherit" onClick={() => void signOut()}>
                  Sign out
                </Button>
              </>
            ) : (
              <Button component={NavLink} to="/sign-in" size="small" variant="contained">
                Sign in
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

function Footer() {
  return (
    <Box
      component="footer"
      sx={{ backgroundColor: "rgba(0,0,0,0.85)", backdropFilter: "blur(12px)" }}
    >
      <Container maxWidth="lg" disableGutters>
        <Box className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-4 text-xs text-white/70 sm:px-6">
          <Typography component="span" variant="inherit" className="font-semibold text-white">
            jones<span style={{ color: jp.navy }}>+</span>palmer
          </Typography>
          <Box className="flex items-center gap-2">
            <BrandMark className="h-4 w-auto shrink-0 opacity-80 brightness-0 invert" />
            <span>{BRAND_TAGLINE}</span>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export function AppShell() {
  return (
    <>
      <VideoBackground />
      <Box className="flex min-h-screen flex-col">
        <Header />
        <Container component="main" maxWidth="lg" className="flex-1 px-4 py-8 sm:px-6">
          <Outlet />
        </Container>
        <Footer />
      </Box>
    </>
  );
}
